import { Card } from "@/components/ui/card";
import type { SDGGoal } from "@/lib/index";

interface SDGCardProps {
  goal: SDGGoal;
}

export function SDGCard({ goal }: SDGCardProps) {
  return (
    <Card className="group relative overflow-hidden border-border/50 bg-card/50 backdrop-blur-sm transition-all duration-200 hover:scale-[1.02] hover:shadow-lg hover:shadow-primary/10 active:scale-[0.97]">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
      <div className="relative p-6">
        <div className="mb-4 flex items-start gap-4">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-primary/10 ring-1 ring-primary/20 transition-all duration-200 group-hover:bg-primary/15 group-hover:ring-primary/30">
            <img 
              src={goal.icon} 
              alt={`ODS ${goal.number}`}
              className="h-12 w-12 object-contain"
            />
          </div>
          <div className="flex-1">
            <div className="mb-1 text-sm font-medium text-primary">
              ODS {goal.number}
            </div>
            <h3 className="text-lg font-semibold text-foreground">
              {goal.title}
            </h3>
          </div>
        </div>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {goal.description}
        </p>
      </div>
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
    </Card>
  );
}

interface FeatureCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
}

export function FeatureCard({ title, description, icon }: FeatureCardProps) {
  return (
    <Card className="group relative overflow-hidden border-border/50 bg-card/50 backdrop-blur-sm transition-all duration-200 hover:scale-[1.02] hover:shadow-lg hover:shadow-secondary/10 active:scale-[0.97]">
      <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 via-transparent to-transparent opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
      <div className="relative p-6">
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-secondary/10 text-secondary ring-1 ring-secondary/20 transition-all duration-200 group-hover:bg-secondary/15 group-hover:ring-secondary/30">
          {icon}
        </div>
        <h3 className="mb-3 text-xl font-semibold text-foreground">
          {title}
        </h3>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {description}
        </p>
      </div>
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-secondary/20 to-transparent" />
    </Card>
  );
}

interface CommitmentCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
}

export function CommitmentCard({ title, description, icon }: CommitmentCardProps) {
  return (
    <Card className="group relative overflow-hidden border-border/50 bg-card/50 backdrop-blur-sm transition-all duration-200 hover:scale-[1.02] hover:shadow-lg hover:shadow-accent/10 active:scale-[0.97]">
      <div className="absolute inset-0 bg-gradient-to-br from-accent/5 via-transparent to-transparent opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
      <div className="relative p-6">
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-accent/10 text-accent ring-1 ring-accent/20 transition-all duration-200 group-hover:bg-accent/15 group-hover:ring-accent/30">
          {icon}
        </div>
        <h3 className="mb-3 text-xl font-semibold text-foreground">
          {title}
        </h3>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {description}
        </p>
      </div>
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent/20 to-transparent" />
    </Card>
  );
}